// CHAT-SWITCH-B (docs/direction/2026-10-28-chat-slack-switch.md): the web side of the wave's
// must-haves that need their own pieces —
// - Send later: the composer's clock opens a time picker; the message waits on the server
//   (`scheduledMessages.*`) and the Saved page lists the waiting ones (edit, cancel).
// - Ask: the search page's "Ask" puts a question to a DE (the Chief of Staff unless another is
//   picked). It answers from the messages this search finds for the asker, and cites them.
// - Incoming webhooks: a channel's managers add one in its details (Integrations); the secret
//   URL shows once, and each hook can be rotated or revoked.
// - Link cards: a message's first links unfurl as cards fetched by the server (no images); its
//   author removes one with the ✕.
import {
  Button,
  Composer,
  type ComposerValue,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Clock3Icon,
  CopyIcon,
  LinkIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SparklesIcon,
  Trash2Icon,
  WebhookIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useCallback, useState } from "react";
import { apiUrl } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { problemText } from "../ops-parts";
import { broadcastOf, MENTION_REF } from "./chat-optimistic";
import { channelQuery, chatKeys } from "./chat-queries";
import { type ChatSearchParams, messageLink } from "./chat-search";
import {
  canonicalBlocks,
  REMIND_PRESETS,
  type RemindPreset,
  refused,
  remindAt,
  timeLabel,
  useBroadcastMentionables,
} from "./chat-switch";
import { type ChatContext, useThreadMentionables } from "./use-chat";

const failed = (title: string) => (e: Error) =>
  toast.add({ title, description: problemText(e, e.message), type: "error" });

/** `yyyy-MM-ddTHH:mm` in local time, for a `datetime-local` input. */
export function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** A time a message may wait for: at least a minute ahead (the server takes 30 seconds). */
export const laterOk = (at: Date | null, now = Date.now()): at is Date =>
  at !== null && !Number.isNaN(at.getTime()) && at.getTime() >= now + 60_000;

const LATER_PRESETS: RemindPreset[] = ["1h", "3h", "tomorrow", "next-week"];

function WhenField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor={id}>When</FieldLabel>
        <Input
          id={id}
          type="datetime-local"
          className="h-10"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <fieldset className="m-0 flex flex-wrap gap-1.5 border-0 p-0" aria-label="Quick times">
        {REMIND_PRESETS.filter((p) => LATER_PRESETS.includes(p.id)).map((p) => (
          <Button
            key={p.id}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange(localInput(remindAt(p.id)))}
          >
            {p.label}
          </Button>
        ))}
      </fieldset>
    </>
  );
}

// Send later -------------------------------------------------------------------------------------

type Later = {
  value: ComposerValue;
  resolve: () => void;
  reject: (e: Error) => void;
};

/** FNV-1a, 2 × 32 bits, as hex: a stable digest of a request (not a secret). */
function digest(s: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c ^ (a >>> 7), 0x01000193) >>> 0;
  }
  return a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

const SCHEDULE_OP_PREFIX = "cos:chat-schedule-op:v1";

const opKey = (co: string, meId: string, body: Record<string, unknown>) => {
  const { send_at: _at, ...what } = body;
  return `${SCHEDULE_OP_PREFIX}:${co}:${meId}:${digest(JSON.stringify(what))}`;
};

/**
 * Codex r2 #5, r3 #3, r4 #2: a Schedule's id, kept in this browser until the server answers it:
 * the same message scheduled again (another dialog, a reload, even another time picked) after an
 * answer was lost reuses it, so the server returns the one already waiting instead of a second.
 * Acknowledged or refused, it is dropped and the next Schedule gets a new id. Codex r5 #2: no
 * expiry — an unanswered one is kept however long it takes to answer it (the server knows its id
 * for good), so a late retry never makes a second schedule.
 */
export function scheduleOpId(co: string, meId: string, body: Record<string, unknown>): string {
  const key = opKey(co, meId, body);
  try {
    const raw = window.localStorage.getItem(key);
    const kept = raw ? (JSON.parse(raw) as { id?: unknown }) : null;
    if (kept && typeof kept.id === "string") return kept.id;
    const id = `sched-op:${crypto.randomUUID()}`;
    window.localStorage.setItem(key, JSON.stringify({ id, at: Date.now() }));
    return id;
  } catch {
    return `sched-op:${crypto.randomUUID()}`;
  }
}

/** The Schedule was answered (or refused): its id is not reused. */
export function settleScheduleOp(co: string, meId: string, body: Record<string, unknown>): void {
  try {
    window.localStorage.removeItem(opKey(co, meId, body));
  } catch {}
}

/**
 * The composer's "Send later": `onSchedule` (absent where a message cannot wait — no thread yet,
 * e.g. a new topic) and the dialog to render. Resolving clears the composer; a cancel keeps it.
 */
export function useSendLater(
  chat: ChatContext,
  threadId: string | null | undefined,
  parent?: string | null,
  /** The conversation's addressee pick, as a send would carry it (THREAD-WAKE). */
  addressee?: () => string | undefined,
): { onSchedule?: (value: ComposerValue) => Promise<void>; dialog: ReactNode } {
  const queryClient = useQueryClient();
  const [later, setLater] = useState<Later | null>(null);
  const [when, setWhen] = useState("");
  const create = useMutation({
    mutationFn: async (v: { value: ComposerValue; at: Date }) => {
      // As a send carries them (Codex r2 #6): @channel / @here and the addressed DE.
      const broadcast = broadcastOf(v.value.mentions);
      const to = addressee?.();
      const body = {
        thread_id: threadId as string,
        body: v.value.blocks as Record<string, unknown>[],
        mentions: v.value.mentions.filter((m) => MENTION_REF.test(m)),
        ...(parent ? { parent_message_id: parent } : {}),
        ...(broadcast ? { broadcast } : {}),
        ...(to ? { addressee: to } : {}),
        send_at: v.at.toISOString(),
      };
      const me = chat.meId ?? "";
      try {
        const made = await chat.api.client.scheduledMessages.create({
          params: { co: chat.co },
          body: { ...body, client_id: scheduleOpId(chat.co, me, body) },
        });
        settleScheduleOp(chat.co, me, body);
        return made;
      } catch (e) {
        // Refused: nothing was made, the next try is a new request. Otherwise (offline, a lost
        // answer) the id stays for the retry.
        if (refused(e)) settleScheduleOp(chat.co, me, body);
        throw e;
      }
    },
    onSuccess: (r) => {
      toast.add({ title: `Scheduled for ${timeLabel(new Date(r.send_at))}`, type: "success" });
      void queryClient.invalidateQueries({ queryKey: chatKeys.scheduled(chat.co) });
    },
    onError: failed("Could not schedule the message"),
  });
  const onSchedule = useCallback(
    (value: ComposerValue) =>
      new Promise<void>((resolve, reject) => {
        setWhen(localInput(remindAt("tomorrow")));
        setLater({ value, resolve, reject });
      }),
    [],
  );
  if (!threadId || chat.readOnly || chat.meId === null) return { dialog: null };
  const at = when ? new Date(when) : null;
  const close = () => {
    later?.reject(new Error("cancelled"));
    setLater(null);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!later || !laterOk(at)) return;
    create.mutate(
      { value: later.value, at },
      {
        onSuccess: () => {
          later.resolve();
          setLater(null);
        },
      },
    );
  };
  const dialog = later ? (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && close()}>
      <DialogContent data-dialog="send-later">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Send later
              <InfoTip label="About scheduled messages">
                It posts as you at that time. Change or cancel it from Saved › Scheduled.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          <WhenField id="later-when" value={when} onChange={setWhen} />
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!laterOk(at) || create.isPending}>
              Schedule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  ) : null;
  return { onSchedule, dialog };
}

type Scheduled = Awaited<
  ReturnType<ChatContext["api"]["client"]["scheduledMessages"]["list"]>
>["data"][number];

/** Saved › Scheduled: the viewer's waiting messages (and failed ones), each editable. */
export function ScheduledSection({ chat }: { chat: ChatContext }) {
  const queryClient = useQueryClient();
  const enabled = chat.meId !== null;
  const list = useQuery({
    queryKey: chatKeys.scheduled(chat.co),
    queryFn: () => chat.api.client.scheduledMessages.list({ params: { co: chat.co } }),
    enabled,
  });
  const [editing, setEditing] = useState<Scheduled | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: chatKeys.scheduled(chat.co) });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      chat.api.client.scheduledMessages.cancel({ params: { co: chat.co, id } }),
    onSuccess: () => toast.add({ title: "Scheduled message cancelled", type: "success" }),
    onError: failed("Could not cancel it"),
    onSettled: refresh,
  });
  if (!enabled) return null;
  const rows = list.data?.data ?? [];
  return (
    <section aria-label="Scheduled" data-section="scheduled" className="mb-4 flex flex-col gap-1">
      <h2 className="flex items-center gap-1.5 px-2 text-xs font-medium text-muted-foreground uppercase">
        Scheduled
        <InfoTip label="About scheduled messages">
          Messages waiting to post, from the clock next to Send.
        </InfoTip>
      </h2>
      {list.isSuccess && !rows.length ? (
        <EmptyState
          variant="compact"
          icon={Clock3Icon}
          title="Nothing scheduled"
          description="Use the clock next to Send."
        />
      ) : null}
      <ul className="flex flex-col">
        {rows.map((r) => {
          const text = (
            <span className="truncate text-sm text-foreground">{r.body_text || "(no text)"}</span>
          );
          return (
            <li
              key={r.id}
              data-scheduled={r.id}
              className="group/scheduled flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
            >
              <Clock3Icon
                aria-hidden={true}
                className={`size-4 shrink-0 ${r.state === "failed" ? "text-destructive" : "text-primary-text"}`}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                {r.topic_id ? (
                  <Link
                    to="/c/$slug/chat/$channelId/$topicId"
                    params={{
                      slug: chat.company.slug,
                      channelId: r.channel_id,
                      topicId: r.topic_id,
                    }}
                    search={(s: Record<string, unknown>) =>
                      ({
                        ...keepFocus(s),
                        ...(r.parent_message_id ? { thread: r.parent_message_id } : {}),
                      }) as never
                    }
                    className="truncate hover:underline"
                  >
                    {text}
                  </Link>
                ) : (
                  text
                )}
                <span className="text-xs text-muted-foreground">
                  {r.state === "failed" ? (
                    <span className="text-destructive">Not sent: {r.failure}</span>
                  ) : (
                    <>Sends {timeLabel(new Date(r.send_at))}</>
                  )}
                </span>
              </div>
              {r.state === "scheduled" ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Edit scheduled message"
                    title="Edit"
                    className="opacity-0 group-hover/scheduled:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                    onClick={() => setEditing(r)}
                  >
                    <PencilIcon />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Cancel scheduled message"
                    title="Cancel"
                    className="opacity-0 group-hover/scheduled:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(r.id)}
                  >
                    <XIcon />
                  </Button>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      {editing ? (
        <EditScheduledDialog chat={chat} row={editing} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}

function EditScheduledDialog({
  chat,
  row,
  onClose,
}: {
  chat: ChatContext;
  row: Scheduled;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [when, setWhen] = useState(() => localInput(new Date(row.send_at)));
  const at = when ? new Date(when) : null;
  // The message's own blocks in the chat editor: formatting, links and mentions kept as written
  // (Codex r3 #9 / r4 #6); what it mentions now is what it will notify.
  const channel = useQuery(channelQuery(chat.api, chat.co, row.channel_id));
  const mentionables = useBroadcastMentionables(
    chat,
    channel.data,
    useThreadMentionables(chat, row.thread_id),
  );
  const changes = (value: ComposerValue) => {
    const broadcast = broadcastOf(value.mentions);
    const bodyChanged =
      JSON.stringify(canonicalBlocks(value.blocks)) !==
      JSON.stringify(canonicalBlocks(row.body as unknown[]));
    return {
      ...(bodyChanged
        ? {
            body: value.blocks as Record<string, unknown>[],
            mentions: value.mentions.filter((m) => MENTION_REF.test(m)),
            broadcast: broadcast ?? null,
          }
        : {}),
      ...(at && at.getTime() !== new Date(row.send_at).getTime()
        ? { send_at: at.toISOString() }
        : {}),
    };
  };
  const save = useMutation({
    mutationFn: (body: ReturnType<typeof changes>) =>
      chat.api.client.scheduledMessages.update({ params: { co: chat.co, id: row.id }, body }),
    onSuccess: () => {
      toast.add({ title: "Scheduled message updated", type: "success" });
      void queryClient.invalidateQueries({ queryKey: chatKeys.scheduled(chat.co) });
      onClose();
    },
    onError: failed("Could not update it"),
  });
  const submit = (value: ComposerValue): Promise<void> => {
    if (!value.text.trim() || !laterOk(at)) return Promise.reject(new Error("not ready"));
    // Codex r5 #7: nothing changed — nothing to save (no empty update).
    const body = changes(value);
    if (!Object.keys(body).length) {
      onClose();
      return Promise.resolve();
    }
    return save.mutateAsync(body).then(() => undefined);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent data-dialog="edit-scheduled">
        <div className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Edit scheduled message</DialogTitle>
          </DialogHeader>
          <WhenField id="scheduled-when" value={when} onChange={setWhen} />
          <Composer
            label="Message"
            sendLabel="Save"
            mentionables={mentionables}
            draft={{ initial: row.body as unknown[], save: () => {} }}
            disabled={save.isPending}
            onSubmit={submit}
          />
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Ask --------------------------------------------------------------------------------------------

type Ask = Awaited<ReturnType<ChatContext["api"]["client"]["chatAsks"]["get"]>>;

/** The DEs a question may go to (active ones), by name. */
export function askableDes(chat: Pick<ChatContext, "members">) {
  return [...chat.members.values()]
    .filter((m) => m.kind === "de" && (m.deStatus === undefined || m.deStatus === "active"))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The search's words and filters as an ask's body (the sources come from this search). */
export function askBody(question: string, de: string, p: ChatSearchParams) {
  return {
    question: question.trim(),
    ...(de ? { de_membership_id: de } : {}),
    ...(p.q ? { q: p.q } : {}),
    ...(p.from ? { from: p.from } : {}),
    ...(p.in ? { in: p.in } : {}),
    ...(p.has ? { has: p.has } : {}),
    ...(p.is ? { is: p.is } : {}),
    ...(p.before ? { before: p.before } : {}),
    ...(p.after ? { after: p.after } : {}),
  };
}

/**
 * The search page's "Ask": the button by the search box and its dialog; `onAsked` gets the new
 * ask's id (the page shows its `AskPanel`).
 */
export function AskButton({
  chat,
  params,
  onAsked,
}: {
  chat: ChatContext;
  params: ChatSearchParams;
  onAsked: (askId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (chat.meId === null || chat.readOnly) return null;
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        <SparklesIcon data-icon="inline-start" />
        Ask
      </Button>
      {open ? (
        <AskDialog
          chat={chat}
          params={params}
          onClose={(id) => {
            setOpen(false);
            if (id) onAsked(id);
          }}
        />
      ) : null}
    </>
  );
}

function AskDialog({
  chat,
  params,
  onClose,
}: {
  chat: ChatContext;
  params: ChatSearchParams;
  onClose: (askId: string | null) => void;
}) {
  const des = askableDes(chat);
  const [question, setQuestion] = useState("");
  const [de, setDe] = useState("");
  const create = useMutation({
    mutationFn: () =>
      chat.api.client.chatAsks.create({
        params: { co: chat.co },
        body: askBody(question, de, params),
      }),
    onSuccess: (r) => onClose(r.id),
    onError: failed("Could not ask"),
  });
  const ok = question.trim().length >= 3;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) create.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose(null)}>
      <DialogContent data-dialog="chat-ask">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Ask about these messages
              <InfoTip label="About Ask">
                The DE reads only the messages this search finds for you, never more than you can
                see, and cites the ones it uses.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="ask-question">Question</FieldLabel>
            <Input
              id="ask-question"
              className="h-10"
              maxLength={500}
              autoFocus
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What did we decide about…?"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="ask-de">Ask</FieldLabel>
            <NativeSelect id="ask-de" value={de} onChange={(e) => setDe(e.target.value)}>
              <NativeSelectOption value="">Chief of Staff</NativeSelectOption>
              {des.map((m) => (
                <NativeSelectOption key={m.id} value={m.id}>
                  {m.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!ok || create.isPending}>
              Ask
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One ask: "Working…" while the DE has it, then its answer and the cited messages. */
export function AskPanel({
  chat,
  askId,
  onClose,
}: {
  chat: ChatContext;
  askId: string;
  onClose: () => void;
}) {
  const ask = useQuery({
    queryKey: chatKeys.ask(chat.co, askId),
    queryFn: () => chat.api.client.chatAsks.get({ params: { co: chat.co, id: askId } }),
    refetchInterval: (q) => (q.state.data?.state === "working" ? 3000 : false),
  });
  const a: Ask | undefined = ask.data;
  const who = a ? (chat.members.get(a.de_membership_id)?.name ?? "The DE") : "The DE";
  return (
    <section
      aria-label="Answer"
      data-slot="chat-ask"
      data-state={a?.state ?? "loading"}
      className="mt-3 flex max-w-3xl flex-col gap-2 rounded-lg border border-border-subtle bg-card p-3"
    >
      <div className="flex items-start gap-2">
        <SparklesIcon aria-hidden={true} className="mt-0.5 size-4 shrink-0 text-primary-text" />
        <p className="m-0 min-w-0 flex-1 text-sm font-medium text-foreground">
          {a?.question ?? "…"}
        </p>
        <Button variant="ghost" size="icon-xs" aria-label="Close answer" onClick={onClose}>
          <XIcon />
        </Button>
      </div>
      {ask.isError ? (
        <p className="m-0 text-sm text-destructive">{problemText(ask.error, "Could not load")}</p>
      ) : !a || a.state === "working" ? (
        <p role="status" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-3.5" />
          Working… {who} is reading {a ? `${a.source_count} messages` : "the messages"}
        </p>
      ) : a.state === "failed" ? (
        <p role="alert" className="m-0 text-sm text-destructive">
          {who} could not answer{a.failure ? `: ${a.failure}` : "."}
        </p>
      ) : (
        <>
          <p className="m-0 text-xs text-muted-foreground">{who} answered</p>
          <div data-slot="ask-answer" className="text-sm whitespace-pre-wrap text-foreground">
            {a.answer}
          </div>
          {a.citations.length ? (
            <div className="flex flex-col gap-1">
              <p className="m-0 text-xs font-medium text-muted-foreground uppercase">Sources</p>
              <ul aria-label="Sources" className="m-0 flex list-none flex-col gap-1 p-0">
                {a.citations.map((c) => (
                  <li key={c.message_id}>
                    <Link
                      {...messageLink(chat, c)}
                      className="flex flex-col rounded-md px-2 py-1 hover:bg-muted"
                    >
                      <span className="text-xs text-muted-foreground">
                        {c.title} ·{" "}
                        {c.author_membership_id
                          ? (chat.members.get(c.author_membership_id)?.name ?? "Someone")
                          : "CompanyOS"}
                      </span>
                      <span className="line-clamp-2 text-sm text-foreground">{c.snippet}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

// Incoming webhooks ------------------------------------------------------------------------------

type Hook = Awaited<
  ReturnType<ChatContext["api"]["client"]["chatWebhooks"]["list"]>
>["data"][number];

/** The full secret URL of a create or rotate answer. */
export function hookUrl(api: Pick<ChatContext["api"], "baseUrl">, path: string): string {
  const base = api.baseUrl ? apiUrl(api.baseUrl, path) : path;
  return new URL(base, window.location.origin).href;
}

/** What the webhooks panel needs of its screen (a chat screen, or the DE's page). */
export type HookHost = Pick<ChatContext, "api" | "co" | "readOnly">;
/** Where a hook posts: a channel, or a DE (its webhook DM; the post wakes it). */
export type HookTarget = { kind: "channel" | "de"; id: string };

/** Channel details › Integrations (its managers): the incoming webhooks. */
export function WebhooksTab({ chat, channel }: { chat: ChatContext; channel: { id: string } }) {
  return <WebhooksPanel chat={chat} target={{ kind: "channel", id: channel.id }} />;
}

/** A target's incoming webhooks: list, add (the URL shown once), rotate, revoke. */
export function WebhooksPanel({ chat, target }: { chat: HookHost; target: HookTarget }) {
  const queryClient = useQueryClient();
  const key = chatKeys.webhooks(chat.co, target.id);
  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      target.kind === "de"
        ? chat.api.client.chatWebhooks.listForDe({ params: { co: chat.co, id: target.id } })
        : chat.api.client.chatWebhooks.list({ params: { co: chat.co, id: target.id } }),
  });
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<{ name: string; url: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "rotate" | "revoke"; hook: Hook } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const show = (r: { webhook: Hook; path: string }) =>
    setSecret({ name: r.webhook.name, url: hookUrl(chat.api, r.path) });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      chat.api.client.chatWebhooks.rotate({ params: { co: chat.co, id } }),
    onSuccess: show,
    onError: failed("Could not rotate the URL"),
    onSettled: refresh,
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      chat.api.client.chatWebhooks.revoke({ params: { co: chat.co, id } }),
    onSuccess: () => toast.add({ title: "Webhook revoked", type: "success" }),
    onError: failed("Could not revoke it"),
    onSettled: refresh,
  });
  const rows = (list.data?.data ?? []).filter((h) => h.revoked_at === null);
  const editable = !chat.readOnly;
  return (
    <div className="flex flex-col gap-3" data-tab="integrations">
      <div className="flex items-center gap-2">
        <h3 className="m-0 flex items-center gap-1.5 text-sm font-medium text-foreground">
          Incoming webhooks
          <InfoTip label="About incoming webhooks">
            {target.kind === "de"
              ? 'Another app sends JSON {"text": "…"} to the hook\'s secret URL. It lands in your DM with this DE and wakes it; the DE treats it as outside data, never as instructions. Anyone with the URL can post: rotate it if it leaks.'
              : 'Another app posts here by sending JSON {"text": "…"} to the hook\'s secret URL. Posts show as the hook\'s name with an Integration label. Anyone with the URL can post: rotate it if it leaks.'}
          </InfoTip>
        </h3>
        {editable ? (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setAdding(true)}>
            <PlusIcon data-icon="inline-start" />
            Add webhook
          </Button>
        ) : null}
      </div>
      {list.isSuccess && !rows.length ? (
        <EmptyState variant="compact" icon={WebhookIcon} title="No webhooks" />
      ) : null}
      <ul className="m-0 flex list-none flex-col p-0">
        {rows.map((h) => (
          <li
            key={h.id}
            data-webhook={h.id}
            className="group/hook flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
          >
            <WebhookIcon aria-hidden={true} className="size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm text-foreground">{h.name}</span>
              <span className="text-xs text-muted-foreground">
                …{h.token_hint} · {h.post_count} posts
                {h.last_used_at ? ` · last ${timeLabel(new Date(h.last_used_at))}` : ""}
              </span>
            </div>
            {editable ? (
              <>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Rotate ${h.name}'s URL`}
                  title="New URL"
                  className="opacity-0 group-hover/hook:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                  disabled={rotate.isPending}
                  onClick={() => setConfirm({ kind: "rotate", hook: h })}
                >
                  <RefreshCwIcon />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Revoke ${h.name}`}
                  title="Revoke"
                  className="opacity-0 group-hover/hook:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                  disabled={revoke.isPending}
                  onClick={() => setConfirm({ kind: "revoke", hook: h })}
                >
                  <Trash2Icon />
                </Button>
              </>
            ) : null}
          </li>
        ))}
      </ul>
      {adding ? (
        <AddWebhookDialog
          chat={chat}
          target={target}
          onClose={(r) => {
            setAdding(false);
            if (r) {
              show(r);
              void refresh();
            }
          }}
        />
      ) : null}
      {secret ? <SecretDialog {...secret} onClose={() => setSecret(null)} /> : null}
      {confirm ? (
        <Dialog open onOpenChange={(o) => !o && setConfirm(null)}>
          <DialogContent data-dialog={`webhook-${confirm.kind}`}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {confirm.kind === "rotate" ? "New URL" : "Revoke webhook"}
                <InfoTip label={confirm.kind === "rotate" ? "About a new URL" : "About revoking"}>
                  {confirm.kind === "rotate"
                    ? `${confirm.hook.name} gets a new secret URL, shown once. The old URL stops working at once: update the app that posts to it.`
                    : `${confirm.hook.name}'s URL stops working for good. Its past posts stay.`}
                </InfoTip>
              </DialogTitle>
            </DialogHeader>
            <DialogFooter>
              <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
              <Button
                type="button"
                variant={confirm.kind === "revoke" ? "destructive" : "default"}
                onClick={() => {
                  if (confirm.kind === "rotate") rotate.mutate(confirm.hook.id);
                  else revoke.mutate(confirm.hook.id);
                  setConfirm(null);
                }}
              >
                {confirm.kind === "rotate" ? "New URL" : "Revoke"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function AddWebhookDialog({
  chat,
  target,
  onClose,
}: {
  chat: HookHost;
  target: HookTarget;
  onClose: (r: { webhook: Hook; path: string } | null) => void;
}) {
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => {
      const input = { params: { co: chat.co, id: target.id }, body: { name: name.trim() } };
      return target.kind === "de"
        ? chat.api.client.chatWebhooks.createForDe(input)
        : chat.api.client.chatWebhooks.create(input);
    },
    onSuccess: (r) => onClose(r),
    onError: failed("Could not add the webhook"),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose(null)}>
      <DialogContent data-dialog="add-webhook">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Add webhook</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="webhook-name">Name</FieldLabel>
            <Input
              id="webhook-name"
              className="h-10"
              maxLength={80}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Deploy bot"
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({ name, url, onClose }: { name: string; url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-dialog="webhook-secret">
        <DialogHeader>
          <DialogTitle>{name}'s URL</DialogTitle>
          <DialogDescription>Shown once. Copy it now.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input readOnly aria-label="Webhook URL" value={url} className="h-10 font-mono text-xs" />
          <Button
            variant="outline"
            onClick={() =>
              void navigator.clipboard?.writeText(url).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
          >
            <CopyIcon data-icon="inline-start" />
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <DialogFooter>
          <DialogClose render={<Button type="button" />}>Done</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Link cards -------------------------------------------------------------------------------------

/** A message worth asking for cards: its text has a web link. */
export const hasWebLink = (text: string): boolean => /\bhttps?:\/\/[^\s<>]/i.test(text);

/** The cards under a message (fetched by the server; no images), each removable by its author. */
export function LinkPreviews({
  chat,
  threadId,
  messageId,
}: {
  chat: ChatContext;
  threadId: string;
  messageId: string;
}) {
  const queryClient = useQueryClient();
  const key = chatKeys.previews(chat.co, messageId);
  const cards = useQuery({
    queryKey: key,
    queryFn: () =>
      chat.api.client.messages.previews({
        params: { co: chat.co, id: threadId, mid: messageId },
      }),
    staleTime: 5 * 60_000,
    // A card still being fetched: ask again a few times, then stop.
    refetchInterval: (q) => (q.state.data?.pending && q.state.dataUpdateCount < 6 ? 1500 : false),
  });
  const hide = useMutation({
    mutationFn: (url: string) =>
      chat.api.client.messages.hidePreview({
        params: { co: chat.co, id: threadId, mid: messageId },
        body: { url },
      }),
    onError: failed("Could not remove the preview"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const data = cards.data;
  if (!data?.data.length) return null;
  return (
    <div data-slot="link-previews" className="flex flex-col gap-1.5">
      {data.data.map((c) => {
        const host = (() => {
          try {
            return new URL(c.url).host;
          } catch {
            return c.url;
          }
        })();
        return (
          <div
            key={c.url}
            data-slot="link-preview"
            className="group/preview relative flex max-w-md flex-col gap-0.5 rounded-md border-l-2 border-border bg-sunken px-3 py-2"
          >
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <LinkIcon aria-hidden={true} className="size-3" />
              {c.site_name ?? host}
            </span>
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-sm font-medium text-primary-text hover:underline"
            >
              {c.title ?? host}
            </a>
            {c.description ? (
              <span className="line-clamp-2 text-xs text-muted-foreground">{c.description}</span>
            ) : null}
            {data.removable ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Remove preview"
                title="Remove preview"
                className="absolute top-1 right-1 opacity-0 group-hover/preview:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                disabled={hide.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  hide.mutate(c.url);
                }}
              >
                <XIcon />
              </Button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
